/**
 * StopCardModals — de modals van een stopkaart, gedeeld door beide renders.
 *
 * Betaling (pin/contant), QR, item bewerken, deels opgehaald, camera en
 * bericht aan kantoor. Krijgt het resultaat van useStopCard; rendert zelf
 * geen kaart. De deels-opgehaald-modal zit hier als WbModal; de logica (het
 * pad dat "niet meegekregen" van "verwijderen" scheidt) staat in useStopCard
 * en verandert niet mee met de UI-laag.
 */

import { DEELS_AFGELEVERD_REDENEN } from '../../lib/constants';
import { WbButton, WbModal, WbTextarea, WbTile } from '../wb';
import { CameraCapture } from '../CameraCapture';
import { PaymentModal } from './PaymentModal';
import { QrPaymentModal } from './QrPaymentModal';
import { EditItemModal } from '../EditItemModal';
import { BerichtComposer } from '../berichten/BerichtComposer';
import type { StopCardProps } from './types';
import type { StopCardState } from './useStopCard';

interface Props {
  stop: StopCardProps['stop'];
  onRefreshRoute?: StopCardProps['onRefreshRoute'];
  s: StopCardState;
}

export function StopCardModals({ stop, onRefreshRoute, s }: Props) {
  return (
    <>
      {/* Payment Modal */}
      <PaymentModal
        isOpen={s.showPaymentModal}
        onClose={() => s.setShowPaymentModal(false)}
        workOrderId={stop.Werk_Order__c || ''}
        workOrderName={s.displayWorkOrderName}
        customerName={s.displayCustomerName}
        openAmount={s.localOpenAmount}
        onPaymentSuccess={s.handlePaymentSuccess}
        onRefreshRoute={onRefreshRoute}
      />

      {/* QR Payment Modal */}
      <QrPaymentModal
        isOpen={s.showQrModal}
        onClose={() => s.setShowQrModal(false)}
        workOrderId={stop.Werk_Order__c || ''}
        paymentUrl={stop.molliePaymentUrl || ''}
        openAmount={s.localOpenAmount}
        onPaymentReceived={s.handlePaymentSuccess}
        onRefreshRoute={onRefreshRoute}
      />

      {/* Edit Item Modal - only for pickup stops */}
      {s.editingItem && (
        <EditItemModal
          item={s.editingItem}
          onClose={() => s.setEditingItem(null)}
          onSaved={async () => {
            console.log('[StopCard] onSaved callback triggered');
            s.setEditingItem(null);
            await s.reloadItems();
            console.log('[StopCard] reloadItems complete');
            if (onRefreshRoute) {
              console.log('[StopCard] calling onRefreshRoute');
              await onRefreshRoute();
              console.log('[StopCard] onRefreshRoute complete');
            }
          }}
        />
      )}

      {/* Deels opgehaald / deels afgeleverd — selecteer welke kleden zijn meegenomen */}
      <WbModal
        open={s.showPartialModal}
        onClose={() => s.setShowPartialModal(false)}
        titel={s.partialRichting === 'afleveren' ? 'Deels afgeleverd' : 'Deels opgehaald'}
        sluitOpAchtergrond={s.partialSelected.size === 0}
        acties={
          <>
            <WbButton variant="secondary" size="lg" onClick={() => s.setShowPartialModal(false)}>Annuleren</WbButton>
            <WbButton size="lg" onClick={s.confirmPartialPickup} disabled={s.partialSelected.size === 0 || !s.partialReasonOk}>Bevestigen</WbButton>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-wb-inkt2">
            {s.partialRichting === 'afleveren'
              ? 'Vink aan welke kleden je hebt afgeleverd. De rest blijft openstaan als nalevering; kantoor plant een nieuwe afleverrit.'
              : 'Vink aan welke kleden je hebt meegenomen. De rest blijft openstaan als na-ophaal.'}
          </p>
          <div className="grid grid-cols-1 gap-1.5">
            {s.partialItems.map((item, idx) => (
              <WbTile key={item.itemId} selected={s.partialSelected.has(item.itemId)} onToggle={() => s.togglePartialItem(item.itemId)}>
                <span className="block font-mono">{item.itemName}</span>
                <span className="block text-sm font-normal text-wb-inkt2">{item.productName || 'Vloerkleed'} {idx + 1}</span>
              </WbTile>
            ))}
          </div>
          {s.partialRichting === 'afleveren' && (
            <section className="space-y-1.5">
              <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2">Waarom bleef het achter?</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {DEELS_AFGELEVERD_REDENEN.map((reden) => (
                  <WbTile key={reden.value} rol="radio" selected={s.partialReason === reden.value} onToggle={() => s.setPartialReason(reden.value)}>{reden.label}</WbTile>
                ))}
              </div>
            </section>
          )}
          <WbTextarea
            label={s.partialRichting === 'afleveren' && s.partialReason === 'Anders' ? 'Toelichting (verplicht)' : 'Reden / opmerking (optioneel)'}
            value={s.partialNotes}
            onChange={(e) => s.setPartialNotes(e.target.value)}
            placeholder={s.partialRichting === 'afleveren' ? 'Bijv. lag nog in de bus' : 'Bijv. meubel op kleed'}
            rows={2}
          />
          <p className="font-mono text-sm text-wb-inkt2">
            {s.partialSelected.size} van {s.partialItems.length} {s.partialRichting === 'afleveren' ? 'afgeleverd' : 'meegenomen'}
            {s.partialSelected.size < s.partialItems.length
              ? ` · ${s.partialItems.length - s.partialSelected.size} ${s.partialRichting === 'afleveren' ? 'blijft in de bus (nalevering)' : 'blijft staan (na-ophaal)'}`
              : ''}
          </p>
        </div>
      </WbModal>

      {/* Media-laag camera (MEDIA_EIGEN) — fase volgt het stop-type */}
      {s.showCamera && stop.Werk_Order__c && (
        <CameraCapture
          fase={stop.Type__c === 'Ophalen' ? 'ophaal' : 'aflevering'}
          doel={{ woSfId: stop.Werk_Order__c }}
          subtitle={s.displayWorkOrderName}
          onClose={() => s.setShowCamera(false)}
        />
      )}

      {/* Bericht aan kantoor */}
      <BerichtComposer
        isOpen={s.showMessageModal}
        onClose={() => s.setShowMessageModal(false)}
        onSent={() => s.setShowMessageModal(false)}
        werkOrder={{
          id: stop.Werk_Order__c || undefined,
          name: s.displayWorkOrderName,
          customerName: s.displayCustomerName,
        }}
      />
    </>
  );
}
